import React, { useState, useRef, useMemo, useContext } from "react";
import axios from "axios";
import { Pagination } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { AiOutlineCloudDownload } from "react-icons/ai";
import Bread from "../../common/Bread";
import { useParams } from "react-router-dom";
import { LangContext } from "../../context/LangContext";
import Loader from "../../common/Loader";
import "./investorRelation.scss";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#003399",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    fontFamily: "DM Sans, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(173, 216, 230, 0.4)",
  },
}));

const InvestorRelation = () => {
  const [isPage, setIsPage] = useState(1);
  const [title, setTitle] = useState("");
  const items = useRef([]);

  const { slug } = useParams();
  const lang = useContext(LangContext);
  const nep = lang.state.np;

  // Dynamic title based on slug + language
  const setDynamicTitle = () => {
    const titles = {
      "agm-minute": nep ? "वार्षिक साधारण सभा मिनेट" : "AGM Minute",
      "annual-report": nep ? "वार्षिक प्रतिवेदन" : "Annual Report",
      "financial-report": nep ? "आर्थिक विवरण" : "Financial Highlights",
      "key-indicators": nep ? "प्रमुख संकेतकहरू" : "Key Indicators",
      "corporate-governance": nep
        ? "कर्पोरेट गभर्नेन्स रिपोर्ट"
        : "Corporate Governance Report",
      "sebon-yearly-report": nep
        ? "सेबोन वार्षिक प्रतिवेदन"
        : "Sebon Yearly Report",
      "sebon-quarterly-report": nep
        ? "सेबोन त्रैमासिक प्रतिवेदन"
        : "Sebon Quarterly Report",
      "letter-to-sebon": nep ? "सेबोनलाई पत्र" : "Letter To Sebon",
      "basel-report": nep ? "बेसल प्रकटीकरण" : "Basel Disclosure",
    };
    setTitle(
      titles[slug] || (nep ? "लगानीकर्तासँगको सम्बन्ध" : "Investor Relation"),
    );
  };

  useMemo(setDynamicTitle, [slug, nep]);

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ["investorData", isPage, slug],
    queryFn: () =>
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/investor_relation/all/${slug}/${isPage}`,
        )
        .then((res) => res.data),
  });

  if (isFetching || isLoading) return <Loader />;
  if (error) return "An error has occurred: " + error.message;

  // Assign current items based on language
  items.current = nep ? data.items.np : data.items.en;

  const breadcrumList = {
    image: "",
    link: "/",
    title,
    l1: nep ? "होम" : "Home",
    l2: nep ? "लगानीकर्तासँगको सम्बन्ध" : "Investor Relation",
    l3: "",
  };

  return (
    <Bread data={breadcrumList}>
      <div className="InterestPageTableContainer" tabIndex={0} role="region" aria-label={nep ? "लगानीकर्ता दस्तावेजहरू" : "Investor documents"}>
        <TableContainer sx={{ border: "1px solid #c2c2c2" }} component={Paper}>
          <Table sx={{ minWidth: 600 }} aria-label={nep ? "लगानीकर्ता दस्तावेजहरू" : "Investor documents"}>
            <caption className="a11y-sr-only">
              {nep ? "लगानीकर्ता दस्तावेजहरू" : "Investor documents"}
            </caption>
            <TableBody>
              {items.current.map((item, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell
                    sx={{ width: "50%" }}
                    align="left"
                    scope="row"
                    className="InterestPageTableCell InterestPageTableTitleText"
                  >
                    {item.title}
                  </StyledTableCell>

                  <StyledTableCell sx={{ width: "25%" }} align="left">
                    <a
                      href={item.DocPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="InterestPageTableCell InterestPageTableActionsButton"
                    >
                      <AiOutlineCloudDownload size="22px" aria-hidden="true" focusable="false" />
                      Download
                      <span className="a11y-sr-only"> {item.title}</span>
                      <span className="a11y-sr-only"> (opens in new window)</span>
                    </a>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {data?.total >= data?.per_page && (
          <Pagination
            style={{ marginTop: "24px" }}
            className="paginationContainer"
            count={Math.ceil(data?.total / data?.per_page)}
            color="primary"
            size="large"
            page={isPage}
            onChange={(e, value) => {
              setIsPage(value);
              window.scrollTo(0, 0);
            }}
          />
        )}
      </div>
    </Bread>
  );
};

export default InvestorRelation;
